/**
 * L'identité de l'éditeur — les mentions que la loi exige avant de vendre.
 *
 * Elles vivaient en clair dans le corps de `Legal.tsx`, mêlées à la copie :
 * dix trous disséminés dans trois cents lignes de JSX, que le vérificateur
 * retrouvait à l'expression régulière. Cela a tenu tant que les remplir était
 * lointain. Le jour où c'est le seul geste qui sépare le produit de la vente
 * publique, l'endroit où on le pose doit être **un** endroit, et il doit dire
 * de lui-même ce qu'il attend.
 *
 * Trois règles tiennent ce module.
 *
 * 1. **`null` veut dire « cette valeur n'existe pas encore »**, jamais « on ne
 *    l'affiche pas ». Une mention nulle est nommée à l'écran, dans une langue
 *    qui s'adresse au visiteur ; c'est le choix du ticket T10 et il ne change
 *    pas. Écrire une chaîne vide contournerait le décompte sans rien publier —
 *    d'où le type, qui interdit l'ambiguïté.
 * 2. **`source` n'est pas un commentaire**, c'est le nom du document où la
 *    valeur se trouve. Une mention dont on ne sait plus où la chercher est une
 *    mention qui ne sera pas remplie.
 * 3. **Rien ici n'est deviné.** Aucune valeur plausible, aucun « à confirmer »
 *    déjà écrit : une mention légale fausse est pire qu'une mention absente,
 *    parce qu'elle a l'air d'avoir été vérifiée.
 *
 * Ce que ce module ne porte **pas** : la durée de conservation d'un numéro.
 * Elle n'est pas une décision à recopier ici mais une valeur que le serveur
 * applique déjà (`CONSERVATION_CONTACTS_JOURS`, lue par la purge nocturne).
 * Elle est publiée par `/api/reglages` et affichée telle quelle — une
 * politique de confidentialité qui annoncerait un délai différent de celui que
 * la purge exécute serait exactement le faux signal de la règle n° 6.
 */

export interface MentionEditeur {
  /**
   * Ce qui s'affiche à la place tant que la valeur manque.
   *
   * Lu par des visiteurs, donc écrit pour eux : « adresse », pas « TODO ».
   */
  readonly etiquette: string;
  /** La valeur publiée, ou `null` tant qu'elle n'existe pas. */
  readonly valeur: string | null;
  /** Le document exact où aller la chercher. Jamais affiché. */
  readonly source: string;
}

/**
 * Les dix mentions, dans l'ordre où elles apparaissent sur la page.
 *
 * L'ordre n'est pas décoratif : c'est celui dans lequel le porteur les lira
 * quand il les remplira, et celui dans lequel le vérificateur les énumère.
 */
export const MENTIONS = {
  formeJuridique: {
    etiquette: "forme juridique",
    valeur: null,
    source: "statuts de la société — SARL, SAS, entreprise individuelle",
  },
  siege: {
    etiquette: "adresse",
    valeur: null,
    source: "statuts ou extrait RCCM — adresse du siège, sans la ville ni le pays",
  },
  rccm: {
    etiquette: "RCCM",
    valeur: null,
    source: "extrait du registre du commerce et du crédit mobilier, numéro complet",
  },
  ncc: {
    etiquette: "NCC",
    valeur: null,
    source: "attestation de la Direction générale des impôts — numéro de compte contribuable",
  },
  responsablePublication: {
    etiquette: "nom",
    valeur: null,
    source: "décision de l'éditeur — la personne physique qui répond du contenu publié",
  },
  regionHebergement: {
    etiquette: "région d'hébergement",
    valeur: null,
    source: "console Google Cloud — la région où vivent Firestore et les fonctions",
  },
  fournisseurModele: {
    etiquette: "fournisseur du modèle",
    valeur: null,
    source: "le fournisseur réellement appelé par le serveur, et le pays où il traite",
  },
  referenceArtci: {
    etiquette: "référence",
    valeur: null,
    source: "récépissé de déclaration ARTCI, numéro et date",
  },
  delaiRemboursement: {
    etiquette: "délai",
    valeur: null,
    source: "décision du porteur — le délai qu'il s'engage à tenir, pas celui qu'il espère",
  },
  juridictionCompetente: {
    etiquette: "ville",
    valeur: null,
    source: "décision du porteur — le tribunal du siège, sauf raison contraire",
  },
} as const satisfies Record<string, MentionEditeur>;

export type CleMention = keyof typeof MENTIONS;

export const CLES_MENTIONS = Object.keys(MENTIONS) as CleMention[];

/**
 * Deux requises, quatre dérivées, quatre facultatives — 08/09/2026.
 *
 * Le miroir de `MENTIONS_REQUISES`, `MENTIONS_DERIVEES` et
 * `MENTIONS_FACULTATIVES` côté serveur (`reglages.ts`), qui en porte la
 * raison. Ici, elles décident de ce que la page dit d'une mention absente :
 * une requise est annoncée « en cours d'obtention », une dérivée arrive
 * toujours du serveur, une facultative absente n'est simplement pas nommée.
 */
export const MENTIONS_REQUISES: readonly CleMention[] = ["formeJuridique", "siege"];

export const MENTIONS_DERIVEES: readonly CleMention[] = [
  "regionHebergement",
  "fournisseurModele",
  "delaiRemboursement",
  "juridictionCompetente",
];

export const MENTIONS_FACULTATIVES: readonly CleMention[] = [
  "rccm",
  "ncc",
  "responsablePublication",
  "referenceArtci",
];

/** Les requises qui manquent encore : c'est ce que l'avertissement annonce. */
export function mentionsRequisesEnAttente(posees: Record<string, string> = {}): CleMention[] {
  return MENTIONS_REQUISES.filter((cle) => valeurMention(cle, posees) === null);
}

/**
 * La clé sous laquelle `/pilotage` pose cette mention — 31/08/2026.
 *
 * Les dix mentions sont devenues posables en exploitation : ce sont des faits
 * inscrits sur des papiers que le porteur a en main, et les publier ne devrait
 * pas dépendre d'une livraison de code. Le serveur les range sous
 * `mentionFormeJuridique`, `mentionSiege`… ; ce préfixe est le seul écart entre
 * les deux vocabulaires, et il est calculé plutôt que recopié dans une seconde
 * table qui dériverait de la première.
 *
 * Un test relie les deux listes, de part et d'autre du réseau
 * (`legalMentions.test.ts`) : une clé ajoutée d'un côté et pas de l'autre
 * publierait une mention que personne ne peut poser, ou l'inverse.
 */
export function cleReglage(cle: CleMention): string {
  return `mention${cle.charAt(0).toUpperCase()}${cle.slice(1)}`;
}

/**
 * Celles qui n'existent pas encore, dans l'ordre de la page.
 *
 * `posees` vient du serveur (`/api/reglages`). Une mention posée en
 * exploitation l'emporte sur la constante du paquet construit, restée `null` :
 * c'est tout l'objet du changement. Sans argument, la fonction rend ce qu'elle
 * rendait avant — utile aux tests, et au rendu tant que la réponse du serveur
 * n'est pas arrivée.
 */
export function mentionsEnAttente(posees: Record<string, string> = {}): CleMention[] {
  return CLES_MENTIONS.filter((cle) => valeurMention(cle, posees) === null);
}

/** La valeur qui fait foi : celle posée en exploitation, sinon celle du paquet. */
export function valeurMention(
  cle: CleMention,
  posees: Record<string, string> = {},
): string | null {
  const posee = posees[cleReglage(cle)];
  if (typeof posee === "string" && posee.trim() !== "") return posee.trim();
  return MENTIONS[cle].valeur;
}

/**
 * Le délai de conservation, tel qu'il peut être écrit sous un engagement.
 *
 * La décision vit ici et non dans le JSX, parce qu'elle y a déjà été perdue une
 * fois : le filtre du lecteur refusait bien un zéro, mais l'écran gardait sa
 * propre condition, et la casser publiait « conservé 0 jours » sans qu'aucun
 * test ne bronche. Zéro veut dire « la purge saute cette classe », jamais
 * « rien n'est gardé » — et l'écrire serait mentir dans le sens qui coûte.
 *
 * Rend `null` quand aucune phrase honnête n'est possible ; l'appelant affiche
 * alors une mention en attente, comme pour les dix autres.
 */
export function delaiConservation(jours: number): string | null {
  if (!Number.isInteger(jours) || jours <= 0) return null;
  return `${jours} jour${jours > 1 ? "s" : ""}`;
}
